import React from 'react';
import { Table, TableBody, TableCell, TableHead, TableRow, Checkbox, Box, Typography } from '@mui/material';
import { IResultLayer, IResultLayerClass } from '../../core/interfaces/result.interface';
import { colorUtils } from '../../Utils/color.utils';
import { distinctivenessColorMap } from '../../Assets/distinctivenessColours';

interface LayerDetailsProps {
  layer: IResultLayer;
  onCheckboxChange: (checked: boolean, classItem: { id: string; classItem: IResultLayerClass }) => void;
}

const getId = (resultId: string, columnName: string, category: string) => {
  return `${resultId}_${columnName}_${category.toString().replace(/\s/g, '-')}`;
};

const LayerDetails: React.FC<LayerDetailsProps> = ({ layer, onCheckboxChange }) => {
  const handleCheckboxChange = (event: React.ChangeEvent<HTMLInputElement>, classItem: IResultLayerClass) => {
    const id = event.target.id;
    onCheckboxChange?.(event.target.checked, { id, classItem });
  };

  return (
    <Table sx={{ width: '100%' }}>
      <TableHead>
        <TableRow sx={{ p: 0, m: 0 }}>
          <TableCell sx={{ p: 0, m: 0 }}>&nbsp;</TableCell>
          <TableCell sx={{ p: 0, m: 0 }}>&nbsp;</TableCell>
          <TableCell sx={{ p: 0, m: 0 }}>&nbsp;</TableCell>
          {layer.details.infoColumns.map(
            (infoColumn, index) =>
              infoColumn.visible && (
                // eslint-disable-next-line react/no-array-index-key
                <TableCell sx={{ width: '60px', p: 0, m: 0, textAlign: 'center' }} key={index}>
                  <Typography sx={{ fontSize: '10px' }}>{infoColumn.displayName}</Typography>
                </TableCell>
              ),
          )}
        </TableRow>
      </TableHead>
      <TableBody>
        {[...layer.details.classes]
          .sort((a, b) => {
            // Only Distinctiveness has this scale to impose. Applied to any other layer it scored
            // every category -1 and left them in the order the result was built with, which is what
            // we want - except 'Medium', which is also a tree Size and so was dragged to the bottom
            // of the Individual Trees list. Other layers keep their built order: area descending for
            // habitats, smallest to largest for trees.
            if (layer.tab?.displayName !== 'Distinctiveness') return 0;

            const order = ['V.Low', 'Low', 'Medium', 'High', 'V.High'];
            return order.indexOf(a.category) - order.indexOf(b.category);
          })
          .map((classItem) => (
            <TableRow key={classItem.category} sx={{ p: 0, m: 0 }}>
              <TableCell sx={{ p: 0, m: 0, textAlign: 'center' }}>
                <Checkbox
                  id={getId(layer.resultId, layer.details.columnName, classItem.category)}
                  checked={classItem.defaultEnabled === undefined || classItem.defaultEnabled}
                  onChange={(event) => handleCheckboxChange(event, classItem)}
                />
              </TableCell>
              <TableCell sx={{ p: 0, m: 0 }}>
                <Box
                  sx={{
                    width: 12,
                    height: 12,
                    textAlign: 'center',
                    backgroundColor:
                      layer.tab?.displayName === 'Distinctiveness'
                        ? distinctivenessColorMap[classItem.category] || classItem.fillColor
                        : classItem.fillColor,
                    opacity: 1 - (classItem.fillTransparency || 0),
                    border: `solid ${classItem.edgeThickness}px ${colorUtils.hexToRGB(
                      layer.tab?.displayName === 'Distinctiveness'
                        ? distinctivenessColorMap[classItem.category] || classItem.edgeColor
                        : classItem.edgeColor,
                      1 - (classItem.edgeTransparency || 0),
                    )}`,
                    borderRadius: '50%',
                  }}
                />
              </TableCell>
              <TableCell sx={{ p: '8px' }}>
                <Typography sx={{ fontSize: '10px', textTransform: 'capitalize' }}>
                  {classItem.displayName}
                </Typography>
              </TableCell>
              {layer.details.infoColumns.map(
                (infoColumn, index) =>
                  infoColumn.visible && (
                    // eslint-disable-next-line react/no-array-index-key
                    <TableCell key={index} sx={{ minWidth: '60px', p: '8px' }}>
                      <Typography sx={{ fontSize: '10px', textAlign: 'center' }}>
                        {(+(classItem.infoValues[index] || 0)).toLocaleString('en-UK', {
                          maximumFractionDigits: 2,
                        })}
                      </Typography>
                    </TableCell>
                  ),
              )}
            </TableRow>
          ))}
      </TableBody>
    </Table>
  );
};

export default LayerDetails;
